import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { TableColumnsType, Tag } from 'antd'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  usePreScreeningServiceDeleteRiskGroupPersonEndpoint,
  usePreScreeningServiceGetRiskGroupPersonsEndpointKey
} from 'src/api/queries'
import {
  GetRiskGroupPersonLinkItem,
  GetRiskGroupResponse,
  RiskGroupStatus
} from 'src/api/requests'
import { TableActionButtons, DeleteConfirm } from 'src/components'
import { CaseStatusTag } from 'src/features/persons/components'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { getPersonRiskGroupStatus } from '../utils'

export const useCandidateConfig = (groupData?: GetRiskGroupResponse) => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const { id } = useParams()

  const queryClient = useQueryClient()

  const { mutate, isPending: isDeleting } =
    usePreScreeningServiceDeleteRiskGroupPersonEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePreScreeningServiceGetRiskGroupPersonsEndpointKey]
        })
      }
    })

  const getActionItems = useMemo(
    () =>
      (data: GetRiskGroupPersonLinkItem, groupData?: GetRiskGroupResponse) => {
        const isNew = groupData?.status === RiskGroupStatus.NEW

        return [
          {
            key: 'edit',
            label: t('common.details'),
            icon: <EditOutlined />,
            onClick: () =>
              navigate(
                paths.riskGroups.personDetails(
                  String(id),
                  String(data?.person?.id)
                )
              )
          },
          ...(isNew
            ? [
                {
                  key: 'delete',
                  label: (
                    <DeleteConfirm
                      title={t('pre_screening.delete_list_title')}
                      onConfirm={() =>
                        mutate({
                          personId: String(data?.person?.id),
                          riskGroupId: String(id)
                        })
                      }
                    />
                  ),
                  danger: true
                }
              ]
            : [])
        ]
      },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'simeCode',
        title: t('common.sime_code'),
        dataIndex: ['person', 'simeCode']
      },
      {
        key: 'tarvCode',
        title: t('common.tarv_code'),
        dataIndex: ['person', 'tarvCode']
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: ['person', 'idnp']
      },
      {
        key: 'last_name',
        title: t('form.last_name'),
        dataIndex: ['person', 'lastName']
      },
      {
        key: 'first_name',
        title: t('form.first_name'),
        dataIndex: ['person', 'firstName']
      },
      {
        key: 'gender',
        title: t('form.gender'),
        render: ({ person }) => person?.gender ?? '-'
      },
      {
        key: 'screeningDate',
        title: t('pre_screening.screening_date'),
        render: ({ screeningDate }) =>
          screeningDate ? formatDate(screeningDate, dayMonthYearBackSlash) : '-'
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ screeningStatus }) => {
          return (
            <Tag
              color={getPersonRiskGroupStatus(t, screeningStatus).color}
              key={screeningStatus}
            >
              {getPersonRiskGroupStatus(t, screeningStatus).text}
            </Tag>
          )
        }
      },
      {
        key: 'caseStatus',
        title: t('persons.case_status.title'),
        render: ({ person }) => <CaseStatusTag status={person?.caseStatus} />
      },
      {
        key: 'actions',
        fixed: 'right',
        render: (data: GetRiskGroupPersonLinkItem) => (
          <TableActionButtons items={getActionItems(data, groupData)} />
        )
      }
    ],
    [t]
  )

  return { columns, isDeleting }
}
